import React, { useEffect, useMemo, useState } from 'react';
import 'react-grid-layout/css/styles.css';
import './WellDiagnosis.scss';
import { Layout } from 'react-grid-layout';
import RGL, { WidthProvider } from 'react-grid-layout';
import { ToastServiceProvider } from '../../components/toast/ToastService';
import { filterWidgetItemnsForTestScreen, GridLayoutItem } from '../../well-chart/WidgetLayoutService';
import Loader from '../common/Loader/Loader';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { RootState } from '../../store';
import { fetchEspDiagnosisData } from '../../services/well-diagnosis/WellDiagnosisService';
import { getMopFromIndustryApplicationId } from '../../utilities/completionsUtils';
import { fetchColumnsData } from '../../dispatchers/ColumnSelectorDispatcher';
import { ReactComponent as WarningIcon } from '../../images/alert-triangle.svg';
import { getWidgetParameters } from '../../utilities/DiagnosisHelper';
import GradientTraverseChart from './well-diagnosis-widgets/GradientTraverseChart';
import PumpCurveChart from './well-diagnosis-widgets/PumpCurveChart';
import ValidatedIPRChart from './well-diagnosis-widgets/ValidatedIPRChart';
import DetailedPumpCurveChart from './well-diagnosis-widgets/DetailedPumpCurveChart';
import DiagnosisTableWidget from './Common/DiagnosisTableWidget';
import {
  mapPredictionsData,
  PredictionColumns,
  WellTestData1Columns,
  getWellTestData,
  WellTestData3Columns,
  WellTestData2Columns,
  getRecommendationsData,
  RecommendationsColumns,
  ResultColumns,
  mapResultData,
} from './Common/DiagnosisTableConfig';
import EmptyState from '../../components/empty-state/EmptyState';
import TestCommentWidget from './well-diagnosis-widgets/TestCommentWidget';
import DiagnosisHeader from './DiagnosisHeader';

const GridLayout = WidthProvider(RGL);

export default function Main() {
  const dispatch = useAppDispatch();
  const wellDiagnosisObject = useAppSelector((state: RootState) => state.testWellColumnsSlice.data?.widgets) || [];
  const isLoading = useAppSelector((state: RootState) => state.testWellColumnsSlice.loading);
  const industryApplicationId = useAppSelector((state: RootState) => state.AssetAnalysis.industryApplicationId);
  const espDiagnosisData = useAppSelector((state: RootState) => state.WellDiagnosis.espDiagnosis.data);
  const espDiagnosisDataLoading = useAppSelector((state: RootState) => state.WellDiagnosis.espDiagnosis.loading);
  const wellId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);

  const layout = 'wellDiagnosis';

  const gradientTraverseParameters = getWidgetParameters(wellDiagnosisObject, 'gradient-traverse-plot');
  const pumpCurveParameters = getWidgetParameters(wellDiagnosisObject, 'pump-curve');
  const validatedIPRPlotParameters = getWidgetParameters(wellDiagnosisObject, 'validated-ipr');
  const pumpCurve1Parameters = getWidgetParameters(wellDiagnosisObject, 'pump-curve-chart1');
  const pumpCurve2Parameters = getWidgetParameters(wellDiagnosisObject, 'pump-curve-chart2');
  const pumpCurve3Parameters = getWidgetParameters(wellDiagnosisObject, 'pump-curve-chart3');
  const gradientTraversePlotData = espDiagnosisData?.GradientTraverseData || [];
  const pumpCurvePlotData = [
    { label: 'Pump 1', data: espDiagnosisData?.Pump1CurveData || [] },
    { label: 'Pump 2', data: espDiagnosisData?.Pump2CurveData || [] },
    { label: 'Pump 3', data: espDiagnosisData?.Pump3CurveData || [] },
  ];

  const validatedIPRPlotData = espDiagnosisData?.IPR1Data || [];
  const pumpCurveChart1Data = espDiagnosisData?.Pump1CurveData || [];
  const pumpCurveChart2Data = espDiagnosisData?.Pump2CurveData || [];
  const pumpCurveChart3Data = espDiagnosisData?.Pump3CurveData || [];
  const PredictionsDegraded = espDiagnosisData?.PredictionsDegraded || {};
  const PredictionsCatalogue = espDiagnosisData?.PredictionsCatalogue || {};
  const LatestRecommendation = espDiagnosisData?.LatestRecommendation || {};
  const PredictionsDegradedData = mapPredictionsData(PredictionsDegraded);
  const PredictionsCatalogueData = mapPredictionsData(PredictionsCatalogue);
  const WellTestData = getWellTestData(espDiagnosisData);
  const RecommendationsData = getRecommendationsData(LatestRecommendation);
  const ResultData = mapResultData(espDiagnosisData);
  const isValid = espDiagnosisData?.IsValid ?? false;

  const [persistedWarnings, setPersistedWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (espDiagnosisData?.Warnings && espDiagnosisData.Warnings.length > 0) {
      setPersistedWarnings(espDiagnosisData.Warnings);
    }
  }, [espDiagnosisData?.Warnings]);

  useEffect(() => {
    const fetchData = async () => {
      const id = typeof industryApplicationId === 'number' ? industryApplicationId : 0;
      const mop = getMopFromIndustryApplicationId(id);
      if (mop) {
        await dispatch(fetchColumnsData(`${mop}-diagnosis`));
      }
    };
    fetchData();
  }, [dispatch, industryApplicationId]);

  useEffect(() => {
    if (industryApplicationId === 4 && wellId !== undefined && wellId !== null) {
      dispatch(fetchEspDiagnosisData({ wellId: wellId }));
    }
  }, [wellId, industryApplicationId, dispatch]);

  const safeLayout = (wellDiagnosisObject: any[], layout: string): Layout[] => {
    wellDiagnosisObject = filterWidgetItemnsForTestScreen(wellDiagnosisObject, layout);
    return wellDiagnosisObject
      ?.map((obj: any, index: number): GridLayoutItem | null => {
        const lg = obj?.layout?.lg;
        if (!lg || !obj?.key) {
          console.warn(`Invalid layout at index ${index}`, obj);
          return null;
        }

        const item: GridLayoutItem = {
          i: String(obj.key),
          x: lg.x,
          y: lg.y,
          w: lg.width,
          h: lg.height,
          minH: lg.minH ?? 1,
          minW: lg.minW ?? 6,
          moduleId: obj.widgetProperty?.moduleId,
        };

        return item;
      })
      .filter((item): item is GridLayoutItem => item !== null);
  };

  const hasRecommendations =
    LatestRecommendation !== null &&
    Array.isArray(RecommendationsData) &&
    RecommendationsData.length > 0 &&
    !RecommendationsData.every(
      (row: any) => !row || Object.values(row).every((v) => v === null || v === undefined || v === ''),
    );

  const layoutMemo = useMemo(() => {
    const baseLayout = safeLayout(wellDiagnosisObject, layout);
    if (!hasRecommendations) {
      return baseLayout.map((item) => {
        if (item.i === 'test-comment') {
          return { ...item, w: 12 };
        }
        if (item.i === 'esp-recommendations') {
          return { ...item, w: 0, h: 0 };
        }
        return item;
      });
    }
    return baseLayout;
  }, [wellDiagnosisObject, layout, hasRecommendations]);

  if (isLoading || espDiagnosisDataLoading) {
    return <Loader />;
  }

  const isEmptyEspDiagnosisData =
    !espDiagnosisData ||
    (typeof espDiagnosisData === 'object' &&
      !Array.isArray(espDiagnosisData) &&
      Object.keys(espDiagnosisData).length === 0) ||
    (Array.isArray(espDiagnosisData) && espDiagnosisData.length === 0);

  if (isEmptyEspDiagnosisData) {
    return (
      <div className='diagnosis-empty-state-container'>
        <EmptyState message='No analyzable tests available' />
      </div>
    );
  }

  return (
    <ToastServiceProvider>
      <div className='diagnosis-component-container'>
        {isValid && <DiagnosisHeader espDiagnosisData={espDiagnosisData} />}
        {persistedWarnings.length > 0 && (
          <div className='diagnosis-warnings'>
            <div className='warnings-header'>
              <WarningIcon className='warning-icon' />
              <div className='warnings-header-text-and-list'>
                <div className='warnings-content'>
                  {persistedWarnings.filter(Boolean).map((warning: string, idx: number) => {
                    const cleanWarning = warning.replace(/<[^>]+>/g, '').trim();
                    const displayWarning = cleanWarning.includes(' - ')
                      ? cleanWarning
                      : cleanWarning.replace(/-+\s*/, ' - ');

                    return (
                      <div className='warnings-header-row' key={idx}>
                        <span className='warnings-header-text'>{displayWarning}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        {isValid && (
          <GridLayout
            layout={layoutMemo}
            cols={12}
            rowHeight={30}
            isDraggable={false}
            isResizable={true}
            className='diagnosis-layout-react-grid'
            useCSSTransforms={false}
            isDroppable={false}
          >
            {layoutMemo?.map((item: any) => (
              <div key={item.i} id={`grid-item-${item.i}`} className='grid-item'>
                {item.i === 'test-comment' && <TestCommentWidget runlife={espDiagnosisData.Runlife ?? ''} />}
                {item.i === 'esp-recommendations' && hasRecommendations ? (
                  <DiagnosisTableWidget
                    widgetHeader='ESP recommendations'
                    columnData={RecommendationsColumns}
                    tableData={RecommendationsData}
                    enableBorder={false}
                    alignCenter={true}
                  />
                ) : null}
                {item.i === 'well-test-data1' && (
                  <>
                    <DiagnosisTableWidget
                      widgetHeader='Well test data'
                      columnData={WellTestData1Columns}
                      tableData={WellTestData}
                      enableBorder={false}
                      alignCenter={true}
                    />
                  </>
                )}
                {item.i === 'well-test-data2' && (
                  <>
                    <DiagnosisTableWidget
                      widgetHeader='Well test data'
                      columnData={WellTestData2Columns}
                      tableData={WellTestData}
                      enableBorder={false}
                      alignCenter={true}
                    />
                  </>
                )}
                {item.i === 'well-test-data3' && (
                  <>
                    <DiagnosisTableWidget
                      widgetHeader='Well test data'
                      columnData={WellTestData3Columns}
                      tableData={WellTestData}
                      enableBorder={false}
                      alignCenter={true}
                    />
                  </>
                )}
                {item.i === 'gradient-traverse-plot' && (
                  <GradientTraverseChart chartData={gradientTraversePlotData} parameters={gradientTraverseParameters} />
                )}
                {item.i === 'pump-curve' && (
                  <PumpCurveChart chartData={pumpCurvePlotData} parameters={pumpCurveParameters} />
                )}
                {item.i === 'validated-ipr' && (
                  <ValidatedIPRChart chartData={validatedIPRPlotData} parameters={validatedIPRPlotParameters} />
                )}
                {item.i === 'results' && (
                  <DiagnosisTableWidget widgetHeader='Results' columnData={ResultColumns} tableData={ResultData} />
                )}
                {item.i === 'rates-at-higher-frequency-degarded-pump' && (
                  <DiagnosisTableWidget
                    widgetHeader='Rates at higher frequency for degraded pump'
                    columnData={PredictionColumns}
                    tableData={PredictionsDegradedData}
                  />
                )}
                {item.i === 'rates-at-higher-frequency-catalogue-pump' && (
                  <DiagnosisTableWidget
                    widgetHeader='Rates at higher frequency for catalogue pump'
                    columnData={PredictionColumns}
                    tableData={PredictionsCatalogueData}
                  />
                )}
                {item.i === 'pump-curve-chart1' && (
                  <DetailedPumpCurveChart
                    chartData={pumpCurveChart1Data}
                    parameter={pumpCurve1Parameters}
                    widgetHeader={espDiagnosisData?.WellName}
                  />
                )}
                {item.i === 'pump-curve-chart2' && (
                  <DetailedPumpCurveChart
                    chartData={pumpCurveChart2Data}
                    parameter={pumpCurve2Parameters}
                    widgetHeader={espDiagnosisData?.WellName}
                  />
                )}
                {item.i === 'pump-curve-chart3' && (
                  <DetailedPumpCurveChart
                    chartData={pumpCurveChart3Data}
                    parameter={pumpCurve3Parameters}
                    widgetHeader={espDiagnosisData?.WellName}
                  />
                )}
              </div>
            ))}
          </GridLayout>
        )}
      </div>
    </ToastServiceProvider>
  );
}
